/**
 * `<page-instance-create>` — the create-instance wizard, routed at
 * `/instances/new`.
 *
 * (The element tag is the one `router.ts` already declares for this route; the
 * module is named for the path it serves.)
 *
 * Implements `SC-F308` (the wizard itself), `SC-F309` (sizing is **one** knob
 * from which the container limits, the reservations and the DuckDB settings are
 * derived together — there are no separate CPU and memory fields, because a
 * DuckDB memory limit above the cgroup limit turns graceful spilling into an
 * OOM kill), `SC-F323` (the Sluice version is pinned from the control plane's
 * own digest allowlist, and the upstream commit and image digest are shown
 * because `concept.md` §1.3's "unmodified upstream build" claim is only as good
 * as its evidence), `SC-F329` (one replica per instance is an invariant, not a
 * default — no surface in this product offers a replica count), `SC-F332`
 * (provisioning is asynchronous and answers a job, watched with the one polling
 * helper interface `I7` exists to keep singular), `SC-F300`, `SC-F901` (a quota
 * refusal names the limit and offers a path) and `SC-F330` (a capacity refusal
 * is a different refusal and says so).
 *
 * The form collects exactly what `SC-S300` allows it to collect: a name, a size
 * and a version. Nothing here maps to `Image`, `Mounts`, `Privileges`,
 * `CapabilityAdd` or `User` — the image is looked up by digest from the
 * allowlist and every other field is fixed by the control plane, which is what
 * makes the Docker socket survivable (`SC-S320`, `SC-S321`).
 *
 * The completion copy is load-bearing and easy to get wrong. A new instance
 * boots into a deliberately useless state, because an empty policy directory is
 * **valid** in Sluice and means deny everything. `concept.md` §6.1 requires the
 * UI to say so in those words rather than reporting a misconfiguration, and to
 * disclose the fail-closed readiness window rather than letting a healthy boot
 * read as a failure.
 *
 * Stories: `SC-S300`, `SC-S301`, `SC-S320`, `SC-S322`, `SC-S900`, `SC-S905`.
 */

import { LitElement, css, html, nothing, type TemplateResult } from "lit";
import { customElement, state } from "lit/decorators.js";

import "../components/index.js";
import type { BnKvRow, BnSelectChangeDetail, BnSelectOption } from "../components/index.js";

import { ApiError, parseError, validationFields } from "../api/errors.js";
import {
  createInstance,
  getInstanceStatus,
  listSluiceVersions,
  sizeClass,
  INSTANCE_SIZES,
  type Instance,
  type InstanceSize,
  type ReconcileStatus,
  type SluiceImage,
} from "../api/instances.js";
import { jobFailure, watchJob, type Job } from "../api/jobs.js";
import { listOrgs, type Organization } from "../api/orgs.js";
import { hrefFor, navigate } from "../nav.js";

/** The three screens this page owns. Provisioning is not a modal: the job runs
 * server-side and the reader may leave at any point (`SC-F332`). */
type Phase = "form" | "provisioning" | "done";

/** Which control a `validation_failed` path belongs to. `06` §1.4.1 fixes the
 * `details.fields[].path` shape package-wide precisely so a form can highlight
 * the right input; a path outside this map is shown as a general message rather
 * than attached to the wrong field. */
const FIELD_FOR_PATH: Record<string, "name" | "size" | "version" | "org"> = {
  name: "name",
  slug: "name",
  size: "size",
  sluice_version: "version",
  org: "org",
};

@customElement("page-instance-create")
export class PageInstanceCreate extends LitElement {
  @state() private phase: Phase = "form";

  /** Organizations the caller **owns**. Creating an instance is owner-level
   * (`SC-F102`), so offering an organization the caller only maintains would be
   * offering a guaranteed 403. */
  @state() private orgs: Organization[] = [];
  /** The published digest allowlist (`SC-F323`). The only source of versions. */
  @state() private versions: SluiceImage[] = [];

  @state() private loading = true;
  /** A failure of the two loads above. The form cannot be shown without them. */
  @state() private loadError: ApiError | null = null;

  @state() private org = "";
  @state() private name = "";
  @state() private size: InstanceSize = "medium";
  @state() private version = "";

  @state() private submitting = false;
  @state() private submitError: ApiError | null = null;
  @state() private fieldErrors: Partial<Record<"name" | "size" | "version" | "org", string>> = {};
  /** `validation_failed` paths this form has no control for. Shown verbatim
   * rather than dropped: a rejected field the user cannot see is a dead end. */
  @state() private otherFieldErrors: string[] = [];

  /** The record as stored, from the `202` body. It carries the slug the server
   * actually assigned, which is the only place the customer learns their
   * hostname (`06` §3.4). */
  @state() private created: Instance | null = null;
  @state() private job: Job | null = null;
  @state() private jobError: ApiError | null = null;
  /** True when the *watch* broke rather than the job — a distinction the copy
   * has to keep, because the job is still running either way. */
  @state() private watchLost = false;

  /** The reconcile loop's own verdict, polled beside the job.
   *
   * A provisioning job reports the sequence THIS tier runs; it cannot report a
   * reconciler that refused the instance's spec, gave up after its failure
   * budget, or was never started at all. Those are the conditions that keep an
   * instance in `provisioning`, and without this the screen shows a spinner and
   * nothing else for as long as anyone is willing to wait. */
  @state() private reconcile: ReconcileStatus | null = null;

  /** Aborts the job watch when the page goes away. */
  private watchAbort?: AbortController;
  /** Stops the status poll when the page goes away. */
  private statusTimer?: ReturnType<typeof setInterval>;

  static override styles = css`
    :host {
      display: block;
      font-family: var(--bino-font-sans);
      color: var(--bino-text);
    }
    .wrap {
      max-width: 46rem;
    }
    h1 {
      margin: 0;
      font-size: var(--bino-font-size-xl);
      font-weight: 600;
    }
    .lede {
      margin: var(--bino-space-xs) 0 var(--bino-space-lg);
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
      max-width: 64ch;
    }
    .section + .section {
      margin-top: var(--bino-space-lg);
    }
    .derived {
      margin-top: var(--bino-space-sm);
    }
    .actions {
      display: flex;
      flex-wrap: wrap;
      gap: var(--bino-space-sm);
      margin-top: var(--bino-space-lg);
    }
    .notes {
      display: grid;
      gap: var(--bino-space-md);
      margin-top: var(--bino-space-lg);
    }
    .notes p,
    .panel-text {
      margin: 0 0 var(--bino-space-sm);
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-muted);
      max-width: 74ch;
    }
    .panel-text:last-child {
      margin-bottom: 0;
    }
    .after-kv {
      margin-top: var(--bino-space-md);
    }
    /* The reconciler's refusal, under the spinner. Warning-coloured rather than
       error-coloured: the create itself succeeded, and what has stalled is the
       convergence behind it. */
    .reconcile-note {
      margin: 0 0 var(--bino-space-sm);
      padding: var(--bino-space-sm);
      border: 1px solid var(--bino-warning-border);
      border-radius: var(--bino-radius-sm);
      background: var(--bino-warning-bg);
      color: var(--bino-warning-text);
      font-size: var(--bino-font-size-md);
      max-width: 74ch;
    }
    ul.panel-text {
      padding-left: var(--bino-space-lg);
    }
    .progress {
      display: flex;
      align-items: center;
      gap: var(--bino-space-md);
      margin-bottom: var(--bino-space-md);
    }
    .step {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-base);
      color: var(--bino-text-secondary);
    }
    code {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-base);
    }
    .meta {
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
    }
  `;

  override connectedCallback() {
    super.connectedCallback();
    void this.loadCatalogues();
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    // The job keeps running server-side; only our watch stops.
    this.watchAbort?.abort();
    this.watchAbort = undefined;
    this.stopStatusPoll();
  }

  /** pollStatus reads the instance's reconcile verdict while the wizard waits.
   *
   * Ten seconds matches the reconciler's own in-motion tier for a `provisioning`
   * instance (`02` §9.1), so a faster poll could only re-read a value that had
   * not moved. Failures are swallowed on purpose: this is a supplementary
   * signal, and turning a transient status read into an error on the create
   * screen would report a problem the create does not have. */
  private startStatusPoll(instanceId: string) {
    this.stopStatusPoll();
    const read = () => {
      void getInstanceStatus(instanceId)
        .then((s) => (this.reconcile = s.reconcile))
        .catch(() => undefined);
    };
    read();
    this.statusTimer = setInterval(read, 10_000);
  }

  private stopStatusPoll() {
    if (this.statusTimer === undefined) return;
    clearInterval(this.statusTimer);
    this.statusTimer = undefined;
  }

  /** The organization roster and the image allowlist. Both are prerequisites:
   * without an owned organization there is nothing to create in, and without the
   * allowlist there is no version to pin, so a failure of either is the page's
   * error state rather than a degraded form. */
  private async loadCatalogues() {
    this.loading = true;
    this.loadError = null;
    try {
      const [orgs, images] = await Promise.all([listOrgs(), listSluiceVersions()]);
      this.orgs = orgs.items.filter((o) => o.role === "owner");
      this.org = this.orgs[0]?.id ?? "";
      this.versions = images.items.filter((v) => !v.revoked);
      this.version = defaultVersion(this.versions);
    } catch (err) {
      this.loadError = parseError(err);
    } finally {
      this.loading = false;
    }
  }

  private get selectedImage(): SluiceImage | undefined {
    return this.versions.find((v) => v.version === this.version);
  }

  private async submit() {
    this.fieldErrors = {};
    this.otherFieldErrors = [];
    this.submitError = null;

    if (this.name.trim() === "") {
      this.fieldErrors = { name: "Give the instance a name." };
      return;
    }
    if (this.version === "") {
      this.fieldErrors = { version: "Choose a Sluice version." };
      return;
    }

    this.submitting = true;
    try {
      const started = await createInstance({
        org: this.org,
        name: this.name.trim(),
        size: this.size,
        sluice_version: this.version,
      });
      this.created = started.instance;
      this.job = started.job;
      this.phase = "provisioning";
      void this.watch(started.job.id);
      this.startStatusPoll(started.instance.id);
    } catch (err) {
      this.applySubmitError(parseError(err));
    } finally {
      this.submitting = false;
    }
  }

  /** applySubmitError puts a refusal where the reader can act on it: on the
   * field that caused it where the envelope names one, and otherwise in a panel
   * that renders the code, the message and the request id. */
  private applySubmitError(err: ApiError) {
    if (err.code === "name_taken") {
      this.fieldErrors = { name: "That name is already used in this organization." };
      return;
    }
    if (err.code === "image_not_allowed") {
      // The server's message names the reason: unknown, revoked or not verified.
      this.fieldErrors = {
        version:
          err.serverMessage ||
          "That version is no longer in the allowed list. Reload the list and pick another.",
      };
      this.submitError = err;
      return;
    }
    if (err.code === "validation_failed") {
      const fields: Partial<Record<"name" | "size" | "version" | "org", string>> = {};
      const others: string[] = [];
      for (const f of validationFields(err)) {
        const target = FIELD_FOR_PATH[f.path];
        if (target) fields[target] = f.reason;
        else others.push(`${f.path}: ${f.reason}`);
      }
      this.fieldErrors = fields;
      this.otherFieldErrors = others;
      if (Object.keys(fields).length === 0 && others.length === 0) this.submitError = err;
      return;
    }
    this.submitError = err;
  }

  /**
   * watch follows the provisioning job with the one progress helper the product
   * has (`I7`). It resolves on **every** terminal state — a failed provision is
   * an outcome this screen renders, not an exception — so the failure is turned
   * back into an `ApiError` by `jobFailure`.
   */
  private async watch(jobId: string) {
    this.watchAbort?.abort();
    const abort = new AbortController();
    this.watchAbort = abort;
    try {
      const done = await watchJob(jobId, {
        signal: abort.signal,
        onUpdate: (j) => (this.job = j),
      });
      this.job = done;
      this.stopStatusPoll();
      const failure = jobFailure(done);
      if (failure) {
        this.jobError = failure;
      } else {
        this.phase = "done";
      }
    } catch (err) {
      if (abort.signal.aborted) return;
      // The *watch* failed, which is not the same thing as the job failing: the
      // job is still running server-side. Saying "provisioning failed" here
      // would be the screen inventing an outcome it does not have.
      this.watchLost = true;
      this.jobError = parseError(err);
    }
  }

  // ── form ────────────────────────────────────────────────────────────────

  /** Enter in the name field submits. The kit's controls live in shadow roots,
   * so a native `<form>` would never see an implicit submission from them; this
   * is what restores the behaviour a keyboard user expects. */
  private onNameKeydown(e: KeyboardEvent) {
    if (e.key !== "Enter" || this.submitting) return;
    e.preventDefault();
    void this.submit();
  }

  private renderOrgField() {
    const options: BnSelectOption[] = this.orgs.map((o) => ({
      value: o.id,
      label: o.name,
      description: o.personal ? "Your personal organization." : undefined,
    }));
    return html`<bn-field
      label="Organization"
      required
      hint="The instance, its policies, its datasources and its audit records belong to this organization."
      .error=${this.fieldErrors.org ?? ""}
    >
      <bn-select
        .options=${options}
        .value=${this.org}
        ?invalid=${!!this.fieldErrors.org}
        @change=${(e: CustomEvent<BnSelectChangeDetail>) => (this.org = e.detail.value)}
      ></bn-select>
    </bn-field>`;
  }

  private renderNameField() {
    return html`<bn-field
      label="Instance name"
      required
      hint="How this instance is listed in Sluice Cloud. Its hostname is derived from the name and assigned when it is created — if the derived label is taken, Sluice Cloud appends a suffix and tells you what it assigned."
      .error=${this.fieldErrors.name ?? ""}
    >
      <bn-input
        .value=${this.name}
        placeholder="acme-prod"
        ?invalid=${!!this.fieldErrors.name}
        @input=${(e: CustomEvent<{ value: string }>) => (this.name = e.detail.value)}
        @keydown=${this.onNameKeydown}
      ></bn-input>
    </bn-field>`;
  }

  private renderSizeField() {
    const options: BnSelectOption[] = INSTANCE_SIZES.map((s) => ({
      value: s.size,
      label: s.label,
      description: `${s.cpu} · ${s.memory} · sized for about ${s.sustained_queries_per_day.toLocaleString()} queries a day`,
    }));
    const chosen = sizeClass(this.size);
    const rows: BnKvRow[] = chosen
      ? [
          { label: "CPU limit", value: chosen.cpu },
          { label: "CPU reserved", value: chosen.cpu_reserved },
          { label: "Memory limit", value: chosen.memory },
          { label: "Memory reserved", value: chosen.memory_reserved },
          { label: "DuckDB memory limit", value: chosen.duckdb_memory },
          { label: "DuckDB threads", value: String(chosen.duckdb_threads) },
          { label: "DuckDB open catalogs", value: String(chosen.duckdb_max_open) },
          {
            label: "State directory",
            value: `${chosen.state_volume} (planning figure, no per-instance quota on this deployment)`,
          },
          { label: "Query spill", value: "spills into the state directory" },
        ]
      : [];
    return html`<bn-field
      label="Size"
      required
      hint="One knob. Everything below is derived from it."
      .error=${this.fieldErrors.size ?? ""}
    >
      <bn-select
        .options=${options}
        .value=${this.size}
        ?invalid=${!!this.fieldErrors.size}
        @change=${(e: CustomEvent<BnSelectChangeDetail>) =>
          (this.size = e.detail.value as InstanceSize)}
      ></bn-select>
    </bn-field>
    ${chosen
      ? html`<bn-panel class="derived" eyebrow="What this size derives">
          <bn-kv .rows=${rows}></bn-kv>
          <p class="panel-text after-kv">
            These are set together and cannot be set separately. A DuckDB memory limit above the
            container limit turns graceful spilling into an out-of-memory kill, and more DuckDB
            threads than scheduled cores turns parallelism into throttling — both look like Sluice
            bugs and neither is one. Changing the size later restarts the instance.
          </p>
        </bn-panel>`
      : nothing}`;
  }

  private renderVersionField() {
    const options: BnSelectOption[] = this.versions.map((v) => ({
      value: v.version,
      label: v.deprecated ? `${v.version} (deprecated)` : v.version,
      description: `${v.channel} · upstream commit ${shortSha(v.source_commit)}${
        v.deprecated ? " · superseded by a newer build" : ""
      }`,
    }));
    const image = this.selectedImage;
    const rows: BnKvRow[] = image
      ? [
          { label: "Version", value: image.version },
          { label: "Channel", value: image.channel },
          { label: "Image digest", value: image.digest },
          { label: "Upstream commit", value: image.source_commit },
          { label: "Published", value: when(image.published) },
          {
            label: "Signature",
            // The list carries verified rows only (`listVersions`).
            value: "verified when the digest was added to the allowlist",
            copyable: false,
          },
        ]
      : [];
    return html`<bn-field
      label="Sluice version"
      required
      hint="Pinned from the control plane's allowlist of published digests. The image is never taken from a tag, and never from anything you can type."
      .error=${this.fieldErrors.version ?? ""}
    >
      <bn-select
        .options=${options}
        .value=${this.version}
        ?invalid=${!!this.fieldErrors.version}
        placeholder="Choose a version"
        @change=${(e: CustomEvent<BnSelectChangeDetail>) => (this.version = e.detail.value)}
      ></bn-select>
    </bn-field>
    ${image
      ? html`<bn-panel class="derived" eyebrow="What will run">
          <bn-kv .rows=${rows}></bn-kv>
          ${image.notes
            ? html`<p class="panel-text after-kv">
                ${image.notes}
                ${image.notes_url
                  ? html`<a href=${image.notes_url} rel="external">Release notes</a>`
                  : nothing}
              </p>`
            : nothing}
          <p class="panel-text after-kv">
            Sluice Cloud runs an unmodified upstream build: this is upstream's own signed release
            image, pulled from ghcr.io/bino-bi/sluice with no Cloud-specific patches. Before the
            digest reached this list, its signature was verified and the commit stamped into the
            image was checked against the commit above. Those two values are the evidence for the
            claim, which is why they are here rather than in a footnote.
          </p>
        </bn-panel>`
      : nothing}`;
  }

  private renderInvariants() {
    return html`<div class="notes">
      <bn-panel eyebrow="One replica, always">
        <p class="panel-text">
          Every instance runs exactly one replica. That is an invariant, not a default you can raise:
          a Sluice process owns its hash-chained audit log and its approval and budget databases
          exclusively, and two processes writing them is corruption rather than a race worth
          tolerating. Capacity is vertical only — the size above — and no screen in Sluice Cloud
          offers a replica count.
        </p>
      </bn-panel>
    </div>`;
  }

  private renderSubmitError() {
    const err = this.submitError;
    if (!err) return nothing;

    if (err.code === "quota_exceeded") {
      const d = (err.details ?? {}) as { quota?: string; limit?: number; current?: number };
      return html`<bn-panel eyebrow="Quota reached">
        <div role="alert">
          <p class="panel-text">
            ${d.quota && d.limit !== undefined && d.current !== undefined
              ? html`This organization is using ${d.current} of ${d.limit} ${d.quota}. Creating
                another is refused until the limit is raised.`
              : err.message}
          </p>
          <p class="panel-text">
            Limits are set per organization by Sluice Cloud operations — plans are not self-service
            yet, so ask your Sluice Cloud contact to raise it.
          </p>
          ${this.renderErrorMeta(err)}
          ${this.org
            ? html`<div class="actions">
                <bn-button @click=${() => navigate(hrefFor("orgs", this.org))}>
                  Organization settings
                </bn-button>
              </div>`
            : nothing}
        </div>
      </bn-panel>`;
    }

    if (err.code === "capacity_unavailable") {
      return html`<bn-panel eyebrow="The cluster cannot place this instance">
        <div role="alert">
          <p class="panel-text">${err.message}</p>
          <p class="panel-text">
            This is a capacity refusal, not a quota one: your organization is allowed another
            instance, but no node can currently reserve the memory this size needs. Nothing was
            created. Try a smaller size, or try again shortly.
          </p>
          ${this.renderErrorMeta(err)}
        </div>
      </bn-panel>`;
    }

    return html`<bn-panel eyebrow="The instance was not created">
      <div role="alert">
        <p class="panel-text">${err.message}</p>
        ${this.renderErrorMeta(err)}
      </div>
    </bn-panel>`;
  }

  /** The two values a support conversation needs: the catalogue code, and the
   * request id `OD-11` puts on every response. */
  private renderErrorMeta(err: ApiError) {
    const rows: BnKvRow[] = [
      { label: "Error code", value: err.code },
      ...(err.requestId ? [{ label: "Request id", value: err.requestId }] : []),
    ];
    return html`<bn-kv .rows=${rows}></bn-kv>
      ${err.requestId
        ? nothing
        : html`<p class="meta">
            The request did not reach Sluice Cloud, so there is no request id to quote.
          </p>`}`;
  }

  private renderForm() {
    return html`
      <bn-card heading="New instance">
        <div class="section">${this.renderOrgField()} ${this.renderNameField()}</div>
        <div class="section">${this.renderSizeField()}</div>
        <div class="section">${this.renderVersionField()}</div>
        ${this.otherFieldErrors.length > 0
          ? html`<bn-panel eyebrow="Rejected values">
              <ul class="panel-text">
                ${this.otherFieldErrors.map((f) => html`<li>${f}</li>`)}
              </ul>
            </bn-panel>`
          : nothing}
        ${this.renderSubmitError()} ${this.renderInvariants()}
        <div class="actions">
          <bn-button variant="primary" ?loading=${this.submitting} @click=${() => void this.submit()}>
            Create instance
          </bn-button>
          <bn-button @click=${() => navigate("/instances")}>Cancel</bn-button>
        </div>
      </bn-card>
    `;
  }

  // ── provisioning ────────────────────────────────────────────────────────

  private renderProvisioning() {
    const inst = this.created;
    const p = this.job?.progress ?? null;
    if (this.jobError) return this.renderJobFailure(this.jobError);
    return html`
      <bn-card heading=${`Provisioning ${inst?.name ?? "the instance"}`}>
        <div class="progress">
          <bn-spinner size="28"></bn-spinner>
          <div role="status" aria-live="polite">
            <div>${p ? p.step : "Waiting for a worker to pick the job up"}</div>
            <div class="step">
              ${p ? `Step ${p.index} of ${p.total}` : `Job ${this.job?.status ?? "queued"}`}
            </div>
          </div>
        </div>
        <p class="panel-text">
          Sluice Cloud is minting this instance's admin token, API-key pepper and audit genesis seed,
          sealing them, materializing them as secrets, allocating its overlay network and hostname,
          creating its state volume and creating the service. It does not block your session:
          provisioning continues if you leave this page.
        </p>
        ${this.renderReconcileNote()}
        ${inst ? html`<bn-kv .rows=${summaryRows(inst)}></bn-kv>` : nothing}
        <div class="actions">
          ${inst
            ? html`<bn-button variant="primary" @click=${() => navigate(hrefFor("instances", inst.id))}>
                Open the instance
              </bn-button>`
            : nothing}
          ${this.job
            ? html`<bn-button @click=${() => navigate(hrefFor("jobs", this.job!.id))}>
                View the job
              </bn-button>`
            : nothing}
        </div>
      </bn-card>
    `;
  }

  /** The reconciler's refusal, shown while the job is still open.
   *
   * This is the honest half of `concept.md` §7 principle 8 on this screen: the
   * paragraph above says objects are being created, and when the loop has told
   * us it cannot create them, saying so beats a spinner that never resolves.
   * `blocked` is called out separately because it means nothing further is
   * attempted — waiting will not help, which the spinner otherwise implies. */
  private renderReconcileNote() {
    const r = this.reconcile;
    if (!r || (!r.error && r.state !== "blocked")) return nothing;
    const blocked = r.state === "blocked";
    return html`
      <div class="reconcile-note" role="status">
        <strong
          >${blocked
            ? "Automatic provisioning has stopped."
            : "The last provisioning attempt did not succeed."}</strong
        >
        ${r.error ? html` ${r.error.message}` : nothing}
        ${blocked
          ? html` Nothing further is attempted automatically; the instance page has the detail.`
          : nothing}
      </div>
    `;
  }

  /** A provisioning job that ended badly — or a watch that lost sight of one,
   * which is a different sentence and gets one. The job's stored error carries
   * no request id by design (a job outlives the request that created it), so the
   * quotable reference here is the job id (`06` §1.8). */
  private renderJobFailure(err: ApiError) {
    const inst = this.created;
    return html`<bn-card
      heading=${this.watchLost ? "Lost track of the provisioning job" : "Provisioning failed"}
    >
      <div role="alert">
        <p class="panel-text">${err.message}</p>
        <bn-kv
          .rows=${[
            { label: "Error code", value: err.code },
            ...(this.job ? [{ label: "Job id", value: this.job.id }] : []),
            ...(inst ? [{ label: "Instance id", value: inst.id }] : []),
          ]}
        ></bn-kv>
        <p class="panel-text">
          ${this.watchLost
            ? "This page stopped receiving updates about the job. The job itself is unaffected and is still running — open the instance or the job to see where it got to."
            : "The instance record still exists in the state the job left it in. Open it to retry or to remove it — nothing is cleaned up behind your back."}
        </p>
        <div class="actions">
          ${inst
            ? html`<bn-button
                variant="primary"
                @click=${() => navigate(hrefFor("instances", inst.id))}
              >
                Open the instance
              </bn-button>`
            : nothing}
          ${this.job
            ? html`<bn-button @click=${() => navigate(hrefFor("jobs", this.job!.id))}>
                View the job
              </bn-button>`
            : nothing}
          <bn-button @click=${() => navigate("/instances")}>Back to instances</bn-button>
        </div>
      </div>
    </bn-card>`;
  }

  // ── completion ──────────────────────────────────────────────────────────

  private renderDone() {
    const inst = this.created;
    if (!inst) return nothing;
    return html`
      <bn-card heading=${`${inst.name} is provisioned`}>
        <bn-kv .rows=${summaryRows(inst)}></bn-kv>

        <div class="notes">
          <bn-panel eyebrow="It denies every query right now">
            <p class="panel-text">
              The instance boots into a deliberately useless state: an empty policy directory is
              valid in Sluice and means <strong>deny everything</strong>. Nothing is broken and there
              is nothing to repair — this is what a Sluice gateway with no policies is supposed to
              do. It starts answering queries the moment you publish a policy that allows something.
            </p>
            <p class="panel-text">
              The usual order is: connect a datasource, author one
              <code>SqlAccessPolicy</code> that allows a group to read a table, then publish.
            </p>
          </bn-panel>

          <bn-panel eyebrow="Expect a few seconds of 503 first">
            <p class="panel-text">
              <code>/v1/ready</code> is fail-closed at boot: datasource health starts false and stays
              false until the first probe sweep completes, so the hostname answers
              <code>503</code> for several seconds after the container starts. That window is
              <strong>Starting</strong>, not Failed, and the instance list labels it that way.
            </p>
          </bn-panel>
        </div>

        <div class="actions">
          <bn-button
            variant="primary"
            @click=${() => navigate(hrefFor("instances", inst.id, "datasources", "new"))}
          >
            Add a datasource
          </bn-button>
          <bn-button @click=${() => navigate(hrefFor("instances", inst.id, "policies", "new"))}>
            Author the first policy
          </bn-button>
          <bn-button @click=${() => navigate(hrefFor("instances", inst.id))}>
            Open the instance
          </bn-button>
        </div>
      </bn-card>
    `;
  }

  // ── shell ───────────────────────────────────────────────────────────────

  private renderLoadError(err: ApiError) {
    return html`<bn-card heading="Could not open the wizard">
      <div role="alert">
        <p class="panel-text">${err.message}</p>
        ${this.renderErrorMeta(err)}
        <div class="actions">
          <bn-button variant="primary" @click=${() => void this.loadCatalogues()}>
            Try again
          </bn-button>
          <bn-button @click=${() => navigate("/instances")}>Back to instances</bn-button>
        </div>
      </div>
    </bn-card>`;
  }

  private renderBody(): TemplateResult | typeof nothing {
    if (this.loading) {
      return html`<bn-spinner block size="28" label="Loading organizations and versions"></bn-spinner>`;
    }
    if (this.loadError) return this.renderLoadError(this.loadError);
    if (this.phase === "provisioning") return this.renderProvisioning();
    if (this.phase === "done") return this.renderDone();
    if (this.orgs.length === 0) {
      return html`<bn-empty-state
        icon="⊘"
        heading="You do not own an organization"
        message="Creating an instance is an owner-level action. Ask an owner of your organization to create it, or create an organization of your own."
      >
        <bn-button slot="actions" @click=${() => navigate("/orgs")}>Organizations</bn-button>
      </bn-empty-state>`;
    }
    if (this.versions.length === 0) {
      return html`<bn-empty-state
        icon="⊘"
        heading="No Sluice version is available to deploy"
        message="Instances only ever run an image digest the control plane has published and verified, and right now that list is empty. Nothing can be provisioned until a version is published."
      >
        <bn-button slot="actions" @click=${() => void this.loadCatalogues()}>Reload</bn-button>
      </bn-empty-state>`;
    }
    return this.renderForm();
  }

  override render() {
    return html`<div class="wrap">
      <h1>Create an instance</h1>
      <p class="lede">
        An instance is one Sluice deployment: a single-tenant gateway on its own hostname, in front of
        the databases you attach to it. It takes three answers — a name, a size and the Sluice build
        to pin.
      </p>
      ${this.renderBody()}
    </div>`;
  }
}

/** The summary every phase after submission shows. The hostname and the query
 * endpoint are the two values a customer leaves with (`SC-S300`, `SC-S905`), and
 * the digest and commit are the provenance evidence (`SC-F323`). */
function summaryRows(inst: Instance): BnKvRow[] {
  return [
    { label: "Name", value: inst.name },
    { label: "Hostname", value: inst.hostname },
    { label: "Query endpoint", value: `https://${inst.hostname}/v1/query` },
    { label: "Size", value: inst.size },
    { label: "Sluice version", value: inst.sluice_version },
    { label: "Image digest", value: inst.image_digest },
    { label: "State", value: inst.observed_state, copyable: false },
  ];
}

/** defaultVersion picks the newest supported build in the `stable` channel, and
 * falls back to whatever is offered rather than leaving the picker empty. The
 * list is not re-sorted: the allowlist route answers newest first. */
function defaultVersion(images: SluiceImage[]): string {
  const stable = images.find((v) => v.channel === "stable" && !v.deprecated);
  return (stable ?? images[0])?.version ?? "";
}

/** shortSha is the 12-character form of a commit SHA — enough to recognise in a
 * one-line description. The full value is always shown in the evidence panel,
 * because that is the one a reader checks against the upstream repository. */
function shortSha(sha: string): string {
  return sha.length > 12 ? sha.slice(0, 12) : sha;
}

/** when renders a timestamp in the reader's locale, falling back to the raw
 * value rather than to "Invalid Date". */
function when(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

declare global {
  interface HTMLElementTagNameMap {
    "page-instance-create": PageInstanceCreate;
  }
}
